import { CapacitorUpdater } from "@capgo/capacitor-updater";
import type {
  BundleInfo,
  BundleListResult,
  LatestVersion
} from "@capgo/capacitor-updater";
import { Cubit } from "blac";
import { logSentryBreadcrumb } from "src/lib/addSentryBreadcrumb";
import envVariables from "src/lib/envVariables";
import { FeatureFlagName, featureFlags } from "src/lib/featureFlags";
import { onAppActive } from "src/hybrid/appLifecycle";
import { isHybridApp } from "src/lib/platform";
import reportErrorSentry from "src/lib/reportErrorSentry";
import { TrackEvent } from "src/state/Track/TrackCubit";
import { tracker } from "src/state/state";
import { ActionSheetController } from "molecule/ActionSheet";
import translate from "src/lib/translate";

export type AppVersionState = {
  current?: {
    bundle: BundleInfo;
  };
  native: string;
  local?: BundleListResult;
  latest?: LatestVersion;
  pluginDetails?: unknown;
};

export type AppUpdateData = {
  updateAvailable: boolean;
  currentVersion?: string | undefined;
  latestVersion?: string | undefined;
  latestBundle?: BundleInfo | undefined;
};

export default class AppVersionBloc extends Cubit<AppVersionState> {
  enabled = isHybridApp();

  constructor() {
    super({
      native: envVariables.BUILD_VERSION
    });
    this.init();
  }

  get enableLogging() {
    return featureFlags.getFlag(FeatureFlagName.loggingOtaUpdates);
  }

  log = (...message: unknown[]) => {
    if (this.enableLogging) {
      // eslint-disable-next-line no-console
      console.warn(`[AppVersionBloc]`, ...message);
    }
    logSentryBreadcrumb("appVersionBloc", ...message);
  };

  init = () => {
    if (!this.enabled) {
      this.log("AppVersionBloc disabled");
      return;
    }
    this.log("AppVersionBloc initialized");
    void CapacitorUpdater.notifyAppReady();
    this.addAppListeners();
    void this.checkPluginConfig();
    void this.checkLatestVersion();
    void this.checkLocalVersions();
    void this.checkCurrentVersion();
  };

  checkPluginConfig = async () => {
    try {
      const pluginDetails = {
        latest: await CapacitorUpdater.getLatest(),
        channel: await CapacitorUpdater.getChannel(),
        getPluginVersion: await CapacitorUpdater.getPluginVersion()
      };
      this.log("Plugin details loaded", pluginDetails);
      this.emit({ ...this.state, pluginDetails });
    } catch (err) {
      this.log("Plugin details error", err);
      reportErrorSentry(err, {}, { expected: false });
    }
  };

  addAppListeners = () => {
    this.log("Adding App listeners");
    onAppActive(() => {
      this.log("App became active");
      void this.loadVersionData();
    });
    void CapacitorUpdater.addListener("downloadComplete", (data) => {
      this.log("Download complete", data);
      tracker.track("OTA Update Downloaded", {
        data: {
          current: this.state.current?.bundle.version,
          version: data.bundle.version
        }
      });
    });
    void CapacitorUpdater.addListener("updateAvailable", (data) => {
      this.log("Update available", data);
      tracker.track("OTA Update Available", {
        data: {
          current: this.state.current?.bundle.version,
          version: data.bundle.version
        }
      });
    });
    void CapacitorUpdater.addListener("updateFailed", (data) => {
      this.log("Update Failed", data);
      reportErrorSentry(new Error("OTA Update Failed"), {
        updateVersion: data.bundle.version,
        updateId: data.bundle.id
      });
    });
  };

  loadVersionData = async () => {
    if (!this.enabled) {
      return;
    }
    this.log("Loading version data", { enabled: this.enabled });
    await this.checkCurrentVersion();
    await this.checkForUpdate();
  };

  setVersionAfterRestart = async (bundle?: BundleInfo) => {
    this.log("Setting version after restart", bundle);
    if (!bundle) {
      return;
    }

    try {
      await CapacitorUpdater.next(bundle);
    } catch (err) {
      reportErrorSentry(err, {}, { expected: false });
    }
  };

  setVersion = async (bundle?: BundleInfo) => {
    this.log("Setting version", bundle);
    if (!bundle) {
      return;
    }

    try {
      await CapacitorUpdater.set(bundle);
    } catch (err) {
      reportErrorSentry(err, {}, { expected: false });
    }
  };

  checkCurrentVersion = async () => {
    try {
      const current = await CapacitorUpdater.current();
      this.emit({ ...this.state, current });
      this.log("Current version", current);
      this.attemptAppUpdate();
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (_err) {
      this.log("Current version error", _err);
      // empty
    }
  };

  checkLatestVersion = async (): Promise<LatestVersion | undefined> => {
    try {
      const latest = await CapacitorUpdater.getLatest();
      this.emit({ ...this.state, latest });
      this.log("Latest version", latest);
      this.attemptAppUpdate();
      return latest;
    } catch (err) {
      this.log("Latest version error", err);
    }
    return undefined;
  };

  checkLocalVersions = async (): Promise<BundleListResult> => {
    try {
      const local = await CapacitorUpdater.list();
      this.emit({ ...this.state, local });
      this.log("Local versions", local);
      this.attemptAppUpdate();
      return local;
    } catch (err) {
      this.log("Local versions error", err);
    }
    return { bundles: [] };
  };

  checkForUpdate = async () => {
    try {
      const latest = await this.checkLatestVersion();
      const list = await this.checkLocalVersions();
      this.log("Checking for update", { latest, list });

      if (!latest) {
        return;
      }

      const latestDownloaded = list.bundles.find(
        (b) => b.version === latest.version
      );

      if (latestDownloaded) {
        tracker.track("OTA Update in cache", {
          data: {
            current: this.state.current?.bundle.version,
            ...latestDownloaded
          }
        });
        this.log("Latest version already downloaded", latestDownloaded);
        return;
      }

      if (latest.url) {
        const data = await CapacitorUpdater.download({
          url: latest.url,
          version: latest.version
        });
        this.log("Downloaded", data);
        tracker.track("OTA Update Downloaded", {
          data: {
            current: this.state.current?.bundle.version,
            ...data
          }
        });
        await this.checkLocalVersions();
      }
    } catch (err) {
      this.log("Check for update error", err);
      reportErrorSentry(err, {}, { expected: false });
    }
  };

  get currentVersionString(): string {
    const web = !isHybridApp();

    if (web) {
      this.log("Web version", envVariables.BUILD_VERSION);
      return envVariables.BUILD_VERSION;
    }

    const { bundle } = this.state.current ?? {};
    const bundleVersion = bundle?.version ?? "";
    const { native } = this.state;

    if (!bundleVersion) {
      this.log("No bundle version", native);
      return native;
    }

    const version = `${bundleVersion} (${native})`;
    this.log("Current version", version);
    return version;
  }

  reset = () => {
    this.log("Resetting");
    CapacitorUpdater.reset()
      .then(() => {
        this.log("Reset complete");
      })
      .catch(this.log);
  };

  attemptAppUpdate = () => {
    const updateData = this.collectNewVersionData();
    this.log("Update data", updateData);

    if (updateData.updateAvailable && updateData.latestBundle) {
      this.log("Scheduling Update", updateData);
      void this.setVersion(updateData.latestBundle);
    }
  };

  collectNewVersionData = (): AppUpdateData => {
    const { current, latest, local } = this.state;
    const collectedData: AppUpdateData = {
      currentVersion: current?.bundle.version,
      latestVersion: latest?.version,
      latestBundle: local?.bundles.find((b) => b.version === latest?.version),
      updateAvailable: false
    };

    collectedData.updateAvailable = Boolean(
      collectedData.currentVersion &&
      collectedData.latestVersion &&
      collectedData.latestBundle &&
      collectedData.currentVersion !== collectedData.latestVersion
    );

    this.log("Collected update data", collectedData);

    return collectedData;
  };

  promptUpdateDialog = async (options: AppUpdateData) => {
    const { currentVersion, latestVersion, latestBundle, updateAvailable } =
      options;

    if (!updateAvailable || this.promptedNewVersion) {
      return;
    }

    this.promptedNewVersion = true;
    try {
      const result = await ActionSheetController.showActions({
        title: translate("appUpdateTitle"),
        message: translate("newVersionAvailable"),
        buttons: [
          { id: "update", title: translate("update") },
          { id: "later", title: translate("later"), style: "cancel" }
        ]
      });

      tracker.track("OTA Update Prompted", {
        data: {
          current: currentVersion,
          latest: latestVersion,
          userAction: result
        }
      });

      if (result === "update") {
        void this.setVersion(latestBundle);
      }
    } catch (error) {
      reportErrorSentry(error, {}, { expected: false });
    }
    this.promptedNewVersion = false;
  };
  promptedNewVersion = false;
}
